import { idbGet, idbSet } from "@/lib/idb";
import api from "@/lib/api";

/**
 * IDB-backed cache for the daily readings.
 *
 * Readings change once per day per locale. The backend already caches
 * upstream calls to the Evangelizo RSS in MongoDB, but every cold reload
 * still pays the cost of a /api/readings round-trip + JSON parse on the
 * phone. Storing the parsed payload in IndexedDB drops that to a single
 * IDB read on warm visits — instant.
 *
 * Cache key: `readings_{YYYY-MM-DD}_{lang}`. Different days don't collide
 * and language switches don't invalidate previously fetched days.
 *
 * Hardening notes (Feb 2026, production bug-fix):
 *   • An "empty" payload (no first_reading AND no gospel) is treated as
 *     a transient failure, not a success: we DON'T persist it to IDB and
 *     we retry up to 3 more times with a 500 ms gap. This prevents the
 *     "/readings shows blank until I scrub the calendar" bug.
 *   • Network requests carry a `_t=Date.now()` cache-buster + a
 *     `Cache-Control: no-cache` header so neither the browser HTTP cache
 *     nor any CDN intermediary can serve a stale empty response.
 */

const VERSION = 1;
const KEY = (date, lang) => `readings_${date}_${lang}`;

// Stale-after window for the same day/lang pair. The backend already
// holds a 7-day MongoDB cache; we re-validate against it once an hour
// during the active day in case someone publishes a correction.
const FRESH_FOR_TODAY_MS = 60 * 60 * 1000; // 1 hour

// Retry policy for the network round-trip. Total attempts = 1 initial +
// MAX_RETRIES additional. 500 ms gap matches the user's spec.
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 500;

// A payload is "usable" if it has either the first reading or the
// gospel. Anything else is treated as an empty upstream response.
function isUsable(payload) {
    if (!payload || typeof payload !== "object") return false;
    return Boolean(
        (payload.first_reading && payload.first_reading.text_html)
        || (payload.gospel && payload.gospel.text_html),
    );
}

function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

async function fetchOnce({ date, lang }) {
    const res = await api.get("/readings", {
        params: { lang, date, _t: Date.now() },
        headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
        },
    });
    return res.data;
}

async function fetchWithRetry({ date, lang }) {
    let lastErr = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
            const data = await fetchOnce({ date, lang });
            if (isUsable(data)) return data;
            // Keep the last empty payload so we can fall back to "show
            // whatever the upstream returned" if every retry is empty too.
            lastErr = null;
            if (attempt < MAX_RETRIES) {
                await sleep(RETRY_DELAY_MS);
                continue;
            }
            // Out of retries — return the empty payload so the UI can
            // show its empty-state instead of an error toast.
            return data;
        } catch (e) {
            lastErr = e;
            if (attempt < MAX_RETRIES) {
                await sleep(RETRY_DELAY_MS);
                continue;
            }
            throw lastErr;
        }
    }
    return null; // unreachable
}

export async function loadReadings({ date, lang, todayISO, force = false }) {
    const key = KEY(date, lang);

    if (!force) {
        try {
            const cached = await idbGet(key);
            if (cached && cached.version === VERSION && cached.payload) {
                // Defensive: ignore previously-cached EMPTY payloads. The
                // earlier release used to persist these and they would
                // stick until the user manually changed dates.
                if (!isUsable(cached.payload)) {
                    // fall through to network
                } else {
                    const isToday = date === todayISO;
                    const age = Date.now() - (cached.savedAt || 0);
                    // Past dates: cache is permanent (the readings won't change).
                    // Today: re-validate after FRESH_FOR_TODAY_MS to catch edits.
                    if (!isToday || age < FRESH_FOR_TODAY_MS) {
                        return { data: cached.payload, source: "idb" };
                    }
                }
            }
        } catch { /* fall through to network */ }
    }

    const data = await fetchWithRetry({ date, lang });
    // Only persist USABLE payloads so a transient empty response can't
    // poison the cache. The next visit will retry from scratch.
    if (isUsable(data)) {
        idbSet(key, VERSION, data).catch(() => {});
    }
    return { data, source: "network" };
}
